import type { FC, ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { Form as AntdForm, type FormProps as AntFormProps, type FormItemProps as AntdFormItemProps } from 'antd';
import { Icon } from '@/components/icon';
import type { StrictOmit } from '@enmon/commons';

const { Item: AntItem, ErrorList, List, Provider, useForm, useFormInstance, useWatch } = AntdForm;

interface FormProps<T = any> extends AntFormProps<T> {
  boldLabels?: boolean;
}

const StyledForm = styled(AntdForm)<FormProps>`
  .ant-form-item-label {
    label {
      white-space: normal;
      word-break: break-word;
      text-align: start;
      ${({ layout }) => layout !== 'vertical' && `width: 170px; max-width: 100%`}
      font-weight: ${({ boldLabels, theme }) => (boldLabels ? theme.font.weight.semibold : theme.font.weight.normal)};

      // Remove : and * from label
      &.ant-form-item-required:before,
      &:after {
        content: none;
      }

      // Required * positioning
      &.ant-form-item-required::after {
        visibility: visible !important;
        content: ' *';
        color: ${({ theme }) => theme.color.status.error};
        font-size: ${({ theme }) => theme.typography.fontSize[2]};
      }
    }
  }

  // Spacing form items
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

export type FormItemProps = StrictOmit<AntdFormItemProps, 'tooltip'> & {
  tooltip?: ReactNode;
};

/** Styled Ant Design Form Component */
export function Form<T>(props: FormProps<T>): JSX.Element {
  return <StyledForm<FC<FormProps<T>>> {...props} labelWrap />;
}

/** Styled Ant Design Item Component */
export const Item: FC<FormItemProps> = ({ tooltip, ...props }) => {
  const theme = useTheme();

  return (
    <AntItem
      {...props}
      tooltip={
        tooltip
          ? {
              title: tooltip,
              showArrow: false,
              icon: <Icon iconString="infoCircle" />,
              overlayStyle: { fontSize: theme.font.typography.metadata.fontSize, padding: '0.5rem' },
            }
          : undefined
      }
    />
  );
};

Form.Item = Item;
Form.useForm = useForm;
Form.useFormInstance = useFormInstance;
Form.useWatch = useWatch;
Form.ErrorList = ErrorList;
Form.List = List;
Form.Provider = Provider;
